import React, { type ReactNode } from 'react';
import { styled } from '@compiled/react';
import InfoIcon from '@atlaskit/icon/glyph/info';
import Link from '@atlaskit/link';
import { Box, Grid, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { fontFallback } from '@atlaskit/theme/typography';
import { token } from '@atlaskit/tokens';
import { ff } from '@atlassian/jira-feature-flagging';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { type SupportedThirdParty, ASANA, MONDAY } from '../../../common/constants/import-type';
import { SectionHeading } from '../../../common/ui';
import { useContentForThirdParty, useUrlForThirdParty } from '../../../common/utils';
import { getHierarchyMappingItems } from '../../../common/utils/third-party-mapping';
import {
	useProjectData,
	useThirdPartyHierarchyState,
} from '../../../controllers/import-state/selectors';
import { AsanaHierarchy, MondayHierarchy } from './illustration';
import messages from './messages';
import { useTableHead, useTableRows } from './utils';

type Props = {
	thirdParty: SupportedThirdParty;
};

const getProjectHierarchyIllustration = (thirdParty: SupportedThirdParty) => {
	switch (thirdParty) {
		case MONDAY:
			return <MondayHierarchy />;
		case ASANA:
			return <AsanaHierarchy />;
		default:
			return null;
	}
};

export const ProjectHierarchy = ({ thirdParty }: Props) => {
	const { formatMessage } = useIntl();
	const [data] = useThirdPartyHierarchyState({
		types: getHierarchyMappingItems(thirdParty),
		formatMessage,
	});

	// return ProjectHierarchyMapping for clickup importer
	// remove fg('jwm_clickup_importer') when clean up ff('jwm-x2c-hierarchy-mapping_kkjnd')
	if (ff('jwm-x2c-hierarchy-mapping_kkjnd') || fg('jwm_clickup_importer')) {
		if (data) {
			return <ProjectHierarchyMapping thirdParty={thirdParty} />;
		}
		return null;
	}
	return <ProjectHierarchyIllustration thirdParty={thirdParty} />;
};

const ProjectHierarchyIllustration = ({ thirdParty }: Props) => {
	const { formatMessage } = useIntl();
	const illustration = getProjectHierarchyIllustration(thirdParty);
	const { hierarchyPageDescription } = useContentForThirdParty(thirdParty);
	const { hierarchyPageDescriptionURL } = useUrlForThirdParty(thirdParty);
	return (
		<Wrapper data-testid="project-create-importer-upload-flow.ui.third-party-review-screen.project-hierarchy.wrapper">
			<SectionHeading>{formatMessage(messages.heading)}</SectionHeading>
			{illustration && <ImageWrapper>{illustration}</ImageWrapper>}
			<ContentWrapper>
				{/* eslint-disable-next-line @atlaskit/design-system/no-legacy-icons */}
				<InfoIcon label="" primaryColor={token('color.background.accent.gray.subtle', '#8590A2')} />
				{hierarchyPageDescription && (
					<Description>
						{formatMessage(hierarchyPageDescription, {
							ul: (chunks: ReactNode[]) => <StyledUl>{chunks}</StyledUl>,
							li: (chunks: ReactNode[]) => <li>{chunks}</li>,
							link: (chunks: ReactNode[]) => (
								<Link href={hierarchyPageDescriptionURL} target="_blank">
									{chunks}
								</Link>
							),
						})}
					</Description>
				)}
			</ContentWrapper>
		</Wrapper>
	);
};

export const ProjectHierarchyMapping = ({ thirdParty }: Props) => {
	const { formatMessage } = useIntl();
	const [{ type }] = useProjectData();
	const { hierarchyMappingDescription } = useContentForThirdParty(thirdParty);
	const { hierarchyPageDescriptionURL } = useUrlForThirdParty(thirdParty, type);
	const tableHead = useTableHead(thirdParty);
	const tableRows = useTableRows(thirdParty);

	return (
		<Box
			xcss={contentWrapperStyles}
			testId="project-create-importer-upload-flow.ui.third-party-review-screen.project-hierarchy.wrapper"
		>
			<SectionHeading>{formatMessage(messages.heading)}</SectionHeading>
			<Box paddingBlockStart="space.200" paddingBlockEnd="space.300">
				{hierarchyMappingDescription &&
					formatMessage(hierarchyMappingDescription, {
						link: (chunks: ReactNode[]) => (
							<Link href={hierarchyPageDescriptionURL} target="_blank">
								{chunks}
							</Link>
						),
					})}
			</Box>
			<Grid
				rowGap="space.100"
				role="table"
				testId="project-create-importer-upload-flow.ui.third-party-review-screen.project-hierarchy.table"
			>
				{tableHead}
				{tableRows}
			</Grid>
		</Box>
	);
};

const contentWrapperStyles = xcss({
	width: '480px',
	paddingTop: 'space.300',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	display: 'flex',
	flexFlow: 'column',
	boxSizing: 'border-box',
	padding: `${token('space.300', '24px')} ${token('space.100', '8px')}`,
	width: '640px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ImageWrapper = styled.div({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	margin: `${token('space.200', '16px')} 0`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.accent.gray.subtlest', colors.N20),
	borderRadius: '4px',
	padding: token('space.250', '20px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentWrapper = styled.div({
	display: 'flex',
	flexDirection: 'row',
	marginLeft: token('space.300', '24px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Description = styled.span({
	display: 'block',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	font: token('font.body', fontFallback.body.medium),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle', colors.N400),
	marginLeft: token('space.100', '8px'),
	flex: 1,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledUl = styled.ul({
	paddingInlineStart: token('space.300', '24px'),
	marginBottom: token('space.300', '24px'),
});
